import TableContainer from "Common/Tables/StringsFiltersTable/TableContainer";
import ActionButton from "components/Buttons/ActionButton";
import { EditButton } from "components/Buttons/EditButton";
import LoaderErrorWrapper from "components/LoaderErrorWrapper/LoaderErrorWrapper";
import ListingPageContainer from "components/PageContainer/ListingPageContainer";
import SearchBox from "components/SearchBox/SearchBox";
import StatusToggle from "components/StatusToggle";
import TruncatedTextCell from "components/TruncatedTextCell/TruncatedTextCell";
import useClearStateOnNavigation from "CustomHooks/useClearStateOnNavigation";
import usePermission from "CustomHooks/usePermission";
import usePreservedTableState from "CustomHooks/usePreservedTableState";
import { app_modules } from "helpers/apps_modules_list";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import { Card, Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { createSelector } from "reselect";
import {
  exportUnitCSV,
  fetchUnits as OnfetchUnits,
  updateStatusInUnit as onUpdateStatusInUnit
} from "slices/unit/thunk";
import Swal from "sweetalert2";
import { showCustomSwal } from "utils/swalHelper";

const Unit = () => {
  document.title = "Unit List";
  const dispatch = useDispatch<any>();
  const location = useLocation();

  const selectUnits = createSelector(
    (state: any) => state.Unit,
    (unit) => ({
      Units: unit.unitList,
      rowCount: unit.rowCount,
      loading: unit.loading,
      error: unit.error,
    })
  );

  const { Units, rowCount, loading, error } = useSelector(selectUnits);

  // Use custom hook to fetch and access permissions
  const { canView, canCreate, canUpdate } = usePermission(
    app_modules?.master?.app_name,
    app_modules?.master?.modules?.unit
  );

  // Use preserved table state hook
  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = usePreservedTableState({
    key: "unit",
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const [data, setData] = useState([]);
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";

  useEffect(() => {
    dispatch(
      OnfetchUnits({
        page: pageIndex + 1,
        pageSize,
        sort: sortField,
        order: sortField ? sortDirection : "",
        search,
        filters: filterValue,
      })
    );
  }, [dispatch, pageIndex, pageSize, sortField, sortDirection, search, filterValue]);

  useEffect(() => {
    setData(Units);
  }, [Units]);

  // Clear state when navigating from create action (not edit)
  useClearStateOnNavigation(location, resetState);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      const trimmedInput = searchInput.trim();

      const dateMatch = trimmedInput.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

      if (dateMatch) {
        const [, day, month, year] = dateMatch;
        const isoDate = `${year}-${month}-${day}`;
        setSearch(isoDate);
      } else {
        setSearch(trimmedInput);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput, setSearch]);

  const handleStatusToggle = useCallback(
    (pkg: any) => {
      const updatedStatus = !pkg.is_active;
      const updatedUnit = {
        id: pkg.unit_id,
        is_active: updatedStatus,
      };
      dispatch(onUpdateStatusInUnit(updatedUnit))
        .unwrap()
        .then(() => {
          toast.success(
            `Unit ${updatedStatus ? "activated" : "deactivated"} successfully`
          );

          dispatch(
            OnfetchUnits({
              page: pageIndex + 1,
              pageSize,
              sort: sortField,
              order: sortField ? sortDirection : "",
              search,
              filters: filterValue,
            })
          );
        })
        .catch((err: any) => {
          toast.error("Failed to update status");
          console.error(err);
        });
    },
    [dispatch, pageIndex, pageSize, sortField, sortDirection, search, filterValue]
  );

  const handleExport = async () => {
    // Show loading modal
    showCustomSwal({
      title: "Exporting...",
      text: "Please wait while we export your data.",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const { blob, filename } = await exportUnitCSV({
        search,
        filters: filterValue,
      });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Replace loading modal with success modal
      showCustomSwal({
        icon: "success",
        title: "Export Completed",
        text: "Export completed successfully.",
      });
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to export CSV";

      // Replace loading modal with error modal
      showCustomSwal({
        icon: "error",
        title: "Export Failed",
        text: errorMessage,
      });
    }
  };

  const DynamicAction =
    canUpdate
      ? [
          {
            header: "Action",
            enableColumnFilter: false,
            enableSorting: false,
            cell: ({ row }: { row: any }) => (
              <EditButton to={`/edit-unit/${row?.original?.unit_id}`} />
            ),
          },
        ]
      : [];

  const columns =
    // useMemo(
    //   () =>
    [
      {
        header: "Sr. No.",
        id: "sr_no",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }: { row: any }) => {
          return (
            row.original.sr_no || row.index + 1 + pageIndex * pageSize || "-"
          );
        },
      },
      {
        header: "Unit Name",
        accessorKey: "unit_name",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "master/unit/dropdown/unit_name/",
      },
      {
        header: "Unit Symbol",
        accessorKey: "unit_symbol",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl: "master/unit/dropdown/unit_symbol/",
      },

      //*// Status field is not added in API res
      {
        header: "Status",
        accessorKey: "is_active",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const pkg = row.original;
          return (
            <StatusToggle
              isActive={pkg.is_active}
              onChange={() => {
                handleStatusToggle(pkg);
              }}
              disabled={!canUpdate}
            />
          );
        },
      },

      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.created_by_user?.username ?? "N/A";
          const date = row.original.created_at
            ? new Date(row.original.created_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const name = row.original.updated_by_user?.username ?? "N/A";
          const date = row.original.updated_at
            ? new Date(row.original.updated_at).toISOString().split("T")[0]
            : "N/A";
          return `${name} - ${date}`;
        },
      },
      ...DynamicAction,
    ];

  return (
    <ListingPageContainer>
      <style>
        {`
          .truncated-text-cell-wrapper {
            min-width: 0 !important;
            display: -webkit-box !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            overflow-wrap: anywhere !important;
            word-break: break-word !important;
            white-space: normal !important;
            line-height: 1.5em !important;
          }
        `}
      </style>
      <Row id="invoiceList">
            <Col lg={12}>
              <Card>
                <Card.Header>
                  <Row className="align-items-center g-2">
                    <Col lg={3} className="me-auto">
                      <h6 className="card-title mb-0">Unit</h6>
                    </Col>

                    {/* Search Input Box (reusable)*/}
                    <Col xl={2} md={3}>
                      <SearchBox
                        value={searchInput}
                        onChange={setSearchInput}
                      />
                    </Col>

                    <Col className="col-md-auto">
                      <div className="hstack gap-2">
                        {canView && (
                          <ActionButton label="Export" icon="bi bi-box-arrow-up" handleClick={handleExport} variant="light" />
                        )}
                        {canCreate && (
                          <ActionButton label="Add Unit" icon="bi bi-plus-circle" navigateLink="/add-unit" variant="primary" />
                        )}
                      </div>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="mt-3">
                  <LoaderErrorWrapper loading={loading} error={error}>
                    <TableContainer
                      columns={columns}
                      data={data}
                      totalRecords={rowCount}
                      pageIndex={pageIndex}
                      pageSize={pageSize}
                      onPageChange={setPageIndex}
                      onPageSizeChange={setPageSize}
                      columnFilters={columnFilters}
                      setColumnFilters={setColumnFilters}
                      sorting={sorting}
                      onSortingChange={setSorting}
                      isBordered={false}
                      isPagination={true}
                      isGlobalFilter={false}
                      SearchPlaceholder="Search Units..."
                      manualSorting={true}
                      colValue={setFilterValue}
                    />
                  </LoaderErrorWrapper>
                </Card.Body>
              </Card>
            </Col>
          </Row>
    </ListingPageContainer>
  );
};

export default Unit;
